import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { formatMoney } from 'ck-utils/price';
import { LazyLoadImage, Typography, Icon } from 'ck-ui';
import { addToCart, getImgixUrl } from 'ck-utils';
import styles from './FreeGift.module.scss';

const Nudge = ({ item, total }) => {
  const {
    tier_value: tierValue,
    tier_min: tierMin,
    promotionbar_after_text: textAfter,
    promotionbar_before_text: textBefore
  } = item;
  const valueTier = Number(tierValue);
  const valueNow = total / valueTier;
  const valueMin = Number(tierMin) || 0;

  // If the total is greater than or equal to the tier value, show the success message
  if (item.tier_value <= total) {
    return (
      <div className={styles.callout}>
        <Icon name="circle-tick" size="medium" color="success" />

        <Typography
          color="white"
          variant="body-large"
          dangerouslySetInnerHTML={{ __html: `${item.promotionbar_after_call_out.replace(/<\/?p>/g, '')} Unlocked` }}
        />
      </div>
    );
  }

  return (
    <>
      <div className={classnames('is-relative', styles.root)}>
        <div style={{ padding: '0 12px' }}>
          <Typography color="white" dangerouslySetInnerHTML={{ __html: textBefore.replace(/<\/?p>/g, '') }} />{' '}
          <span className="has-text-gold-dark">{formatMoney(tierValue - total)}</span>{' '}
          <Typography color="white" dangerouslySetInnerHTML={{ __html: textAfter.replace(/<\/?p>/g, '') }} />
        </div>
        <div className={styles.progress}>
          <div
            aria-label="progressbar"
            role="progressbar"
            aria-valuenow={valueNow}
            aria-valuemin={valueMin}
            aria-valuemax={100}
            style={{ width: `${valueNow * 100}%` }}
            className={styles.bar}
          />
        </div>
      </div>
    </>
  );
};

Nudge.propTypes = {
  item: PropTypes.object.isRequired,
  total: PropTypes.number.isRequired
};

const FreeGift = ({ item, total, showAddToCart }) => {
  const { image, handle, isBelowTierThreshold, title, variants, tier_value: tierValue, tier_min: tierMin } = item;
  const valueTier = Number(tierValue);
  const valueNow = isBelowTierThreshold ? total / valueTier : 0;
  const valueMin = Number(tierMin) || 0;

  return (
    <>
      <div className={classnames('is-flex is-relative', styles.root, { [styles.successBg]: !isBelowTierThreshold })}>
        <div className={styles.width}>
          <div className={classnames('image-wrapper', styles.imgWrapper)}>
            {image && (
              <LazyLoadImage
                src={`${getImgixUrl(image, [
                  'fit=crop',
                  'h=400',
                  'w=320'
                ])}`}
                alt={title}
              />
            )}
          </div>
        </div>
        <div className={classnames('is-flex-1', styles.right)}>
          {isBelowTierThreshold ? (
            <Typography color="white" display="block">
              Spend an extra <span className="has-text-gold-dark">{formatMoney(valueTier - total)}</span> and cop this
            </Typography>
          ) : (
            <Typography color="white" display="block">
              You’re eligible to cop this
            </Typography>
          )}
          <Typography renderAs="a" color="white" variant="heading7" uppercase href={`/products/${handle}`}>
            {title}
          </Typography>
          <Typography color="white" display="block">
            For free
          </Typography>

          <div className="is-relative mt-2">
            {showAddToCart && (
              <>
                <select
                  aria-label="Add To Cart"
                  className={classnames('is-uppercase is-full-width', styles.select)}
                  disabled={isBelowTierThreshold}
                  onChange={(e) => addToCart(e.target.value, 1, window.ajaxCart.load)}
                >
                  <option className="blank" value="">Add To Cart</option>
                  {variants.map((variant) => (
                    <option
                      key={variant.id}
                      value={variant.id}
                      disabled={!variant.available}
                    >
                      {variant.title}
                      {variant.available ? null : ' - SOLD OUT'}
                    </option>
                  ))}
                </select>
                <Icon
                  name="arrow-down"
                  color="secondary"
                  size="small"
                  className={styles.icon}
                />
              </>
            )}
          </div>
        </div>
        {isBelowTierThreshold && (
          <div className={styles.progress}>
            <div
              aria-label="progressbar"
              role="progressbar"
              aria-valuenow={valueNow}
              aria-valuemin={valueMin}
              aria-valuemax={100}
              style={{ width: `${valueNow * 100}%` }}
              className={styles.bar}
            />
          </div>
        )}
      </div>
    </>
  );
};

FreeGift.propTypes = {
  item: PropTypes.object.isRequired,
  showAddToCart: PropTypes.bool,
  total: PropTypes.number
};

FreeGift.defaultProps = {
  total: null,
  showAddToCart: true
};

export { Nudge };
export { FreeGift };
